if (!customElements.get('flipzy-base-addon')) {
  customElements.define('flipzy-base-addon', class extends HTMLElement {
    async connectedCallback() {
      if (!this.priceRecovery) {
        this.priceRecovery = () => this.connectedCallback();
        document.addEventListener('flipzy:price-test-recovered', this.priceRecovery);
      }
      if (this.initialized || this.initializing) return this.initializing;
      this.initializing = this.initialize();
      try { await this.initializing; }
      finally { this.initializing = null; }
    }

    async initialize() {
      if (window.FlipzyPriceTestBoot?.blocked) return;
      this.form = this.closest('form');
      this.productForm = this.closest('product-form');
      if (!this.form || !this.productForm) return;
      if (window.FlipzyPriceTest) {
        await window.FlipzyPriceTest.ready;
        if (this.isConnected === false) return;
        if (window.FlipzyPriceTest.state.blocked && !window.FlipzyPriceTest.state.recoverableDiscount) return;
      }
      this.events?.abort();
      this.events = new AbortController();
      const { signal } = this.events;
      this.enabled = this.querySelector('[data-base-enabled]');
      this.variant = this.querySelector('[data-base-variant]');
      this.quantity = this.querySelector('[data-base-quantity]');
      this.button = this.form.querySelector('[type="submit"]');
      this.buttonLabel = this.button.querySelector('span');
      this.originalLabel = this.buttonLabel.textContent;
      this.productInfo = this.closest('product-info');
      this.card = this.closest('.flipzy-purchase-card');
      this.packQuantity = this.productInfo?.querySelector('[name="quantity"]');
      this.packPrice = Number(this.dataset.packPrice);
      this.mountPurchaseControls();
      this.addEventListener('change', () => this.update(), { signal });
      this.addEventListener('input', () => this.updateSummary(), { signal });
      this.addEventListener('click', (event) => {
        if (event.target.closest('button, [data-base-enabled]')) requestAnimationFrame(() => this.alignPurchase());
      }, { signal });
      if (typeof subscribe === 'function') {
        this.variantUnsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (data.sectionId !== this.dataset.section) return;
          this.packPrice = Number(data.variant?.price);
          this.updateSummary();
        });
        this.quantityUnsubscribe = subscribe(PUB_SUB_EVENTS.quantityUpdate, () => this.updateSummary());
      }
      // Only the opted-in two-product purchase is intercepted. Pack-only stays native Dawn.
      this.form.addEventListener('submit', (event) => this.submit(event), { capture: true, signal });
      this.querySelector('.flipzy-base-addon__enhanced').hidden = false;
      this.dataset.ready = '';
      this.update();
      this.initialized = true;
    }

    mountPurchaseControls() {
      // Move existing Dawn nodes, retaining IDs, form association and quantity rules.
      const price = this.productInfo?.querySelector(`[id="price-${this.dataset.section}"]`);
      const quantity = this.packQuantity?.closest('.product-form__quantity');
      const priceSlot = this.querySelector('[data-pack-price-slot]');
      const quantitySlot = this.querySelector('[data-pack-quantity-slot]');
      // Shopify can briefly serve an older snippet while a theme update propagates.
      if (!priceSlot || !quantitySlot) return;
      if (price) priceSlot.append(price);
      if (quantity) quantitySlot.append(quantity);
      this.productInfo?.classList.add('flipzy-purchase-enhanced');
      this.card?.classList.add('is-ready');
    }

    update() {
      const option = this.variant.selectedOptions[0];
      this.querySelector('[data-base-options]').hidden = !this.enabled.checked;
      this.querySelector('[data-base-price]').textContent = window.FlipzyPriceTest?.state.active
        ? new Intl.NumberFormat(this.dataset.locale, { style: 'currency', currency: this.dataset.currency }).format(
          window.FlipzyPriceTest.unitPrice('base', Number(option.dataset.cents)) / 100)
        : option.dataset.price;
      this.quantity.min = option.dataset.min;
      this.quantity.step = option.dataset.step;
      if (option.dataset.max) this.quantity.max = option.dataset.max;
      else this.quantity.removeAttribute('max');
      this.quantity.disabled = !this.enabled.checked;
      this.updateSummary();
    }

    selectionTotals(packCount, baseCount, packPrice, basePrice) {
      if (window.FlipzyPriceTest) {
        packPrice = window.FlipzyPriceTest.unitPrice('pack', packPrice);
        basePrice = window.FlipzyPriceTest.unitPrice('base', basePrice);
      }
      const included = this.dataset.promotion || this.dataset.baseVolumeDiscounts === 'true' ? Math.min(packCount, baseCount) : 0;
      const saving = included * packPrice;
      const bases = window.FlipzyOfferPricing.baseTotals(
        basePrice, baseCount, this.dataset.currency || 'EUR', this.dataset.baseVolumeDiscounts === 'true'
      );
      return { included, saving, packs: packCount * packPrice, bases: bases.total, baseDiscountPercent: bases.percent,
        total: packCount * packPrice + bases.total - saving };
    }

    updateSummary() {
      if (!this.packQuantity || !this.querySelector('.flipzy-base-addon__summary')) {
        if (!this.button.disabled) this.buttonLabel.textContent = this.enabled.checked ? this.dataset.buttonLabel : this.originalLabel;
        return;
      }
      const packCount = Number(this.packQuantity.value);
      const baseCount = this.enabled.checked ? Number(this.quantity.value) : 0;
      const basePrice = Number(this.variant.selectedOptions[0].dataset.cents);
      const valid = Number.isInteger(packCount) && packCount > 0 && Number.isInteger(baseCount) && baseCount >= 0 &&
        (!this.enabled.checked || baseCount > 0) && Number.isFinite(this.packPrice) && Number.isFinite(basePrice);
      this.querySelector('.flipzy-base-addon__summary').hidden = !valid;
      if (!valid) {
        this.querySelector('[data-selection-offer]').hidden = true;
        if (!this.button.disabled) this.buttonLabel.textContent = this.dataset.addLabel;
        return;
      }
      const totals = this.selectionTotals(packCount, baseCount, this.packPrice, basePrice);
      const formatter = new Intl.NumberFormat(this.dataset.locale, { style: 'currency', currency: this.dataset.currency });
      const money = (cents) => formatter.format(cents / 100);
      const text = (selector, value) => { this.querySelector(selector).textContent = value; };
      text('[data-selection-pack]', `${packCount} × ${this.dataset.packTitle}`);
      text('[data-selection-pack-price]', money(totals.packs));
      text('[data-selection-base]', `${baseCount} × ${this.dataset.baseTitle}${totals.baseDiscountPercent
        ? ` · ${this.dataset.percentOff.replace('[percent]', totals.baseDiscountPercent)}` : ''}`);
      text('[data-selection-base-price]', money(totals.bases));
      text('[data-selection-saving]', this.dataset.includedLabel.replace('[count]', totals.included));
      text('[data-selection-saving-price]', `−${money(totals.saving)}`);
      text('[data-selection-total]', money(totals.total));
      this.querySelector('[data-selection-base-row]').hidden = baseCount === 0;
      this.querySelector('[data-selection-saving-row]').hidden = totals.included === 0;
      this.querySelector('[data-selection-offer]').hidden = totals.included === 0;
      const shipping = this.querySelector('[data-selection-shipping]');
      if (shipping) shipping.hidden = this.dataset.baseVolumeDiscounts !== 'true' || baseCount < 2;
      if (!this.button.disabled) this.buttonLabel.textContent = `${this.dataset.addLabel} · ${money(totals.total)}`;
    }

    alignPurchase() {
      // A single, small alignment after an explicit choice; never snap ordinary page scrolling.
      if (this.aligned || !this.card || !window.matchMedia('(max-width: 749px)').matches ||
        window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const rect = this.card.getBoundingClientRect();
      const height = window.visualViewport?.height || window.innerHeight;
      const header = document.querySelector('.section-header')?.getBoundingClientRect().height || 80;
      if (rect.height > height - header - 32) return;
      const distance = rect.top - header - 16;
      if ((rect.bottom > height - 16 || rect.top < header) && Math.abs(distance) < height / 2) {
        this.aligned = true;
        window.scrollBy({ top: distance, behavior: 'smooth' });
      }
    }

    async request(url, body) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 20000);
      try {
        const response = await fetch(url, { ...(body ? {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body),
        } : { headers: { Accept: 'application/json' }, cache: 'no-store' }), signal: controller.signal });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || this.dataset.error);
        return result;
      } catch (error) {
        const failure = new Error(error.name === 'AbortError' ? this.dataset.error : error.message || this.dataset.error);
        failure.uncertainCart = Boolean(body);
        throw failure;
      } finally { clearTimeout(timer); }
    }

    async submit(event) {
      if (!this.enabled.checked) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (this.busy || this.button.disabled || this.button.getAttribute('aria-disabled') === 'true') return;
      if (!this.quantity.reportValidity() || this.variant.selectedOptions[0].disabled) return;
      const data = new FormData(this.form);
      const pack = { id: data.get('id'), quantity: Number(data.get('quantity') || 1) };
      if (!pack.id || !Number.isInteger(pack.quantity) || pack.quantity < 1) return;
      if (data.get('selling_plan')) pack.selling_plan = data.get('selling_plan');
      for (const [key, value] of data) {
        const property = key.match(/^properties\[(.+)\]$/);
        if (property) (pack.properties ||= {})[property[1]] = value;
      }
      this.busy = true;
      this.productForm.handleErrorMessage();
      this.button.setAttribute('aria-disabled', 'true');
      this.button.classList.add('loading');
      const spinner = this.button.querySelector('.loading__spinner');
      spinner?.classList.remove('hidden');
      const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      cart?.setActiveElement(this.button);
      const rendering = cart ? { sections: cart.getSectionsToRender().map((section) => section.id), sections_url: location.pathname } : {};
      let added = false;
      let reloadPending = false;
      try {
        if (window.FlipzyPriceTest) {
          await window.FlipzyPriceTest.prepareCart();
          if (window.FlipzyPriceTest.state.active) pack.properties = { ...pack.properties, ...window.FlipzyPriceTest.properties() };
        }
        const before = await this.request(`${routes.cart_url}.js`);
        const quoted = this.selectionTotals(pack.quantity, Number(this.quantity.value),
          Number(this.packPrice), Number(this.variant.selectedOptions[0].dataset.cents));
        let result = await this.request(routes.cart_add_url, {
          items: [pack, { id: this.variant.value, quantity: Number(this.quantity.value),
            ...(window.FlipzyPriceTest?.state.active ? { properties: window.FlipzyPriceTest.properties() } : {}) }], ...rendering,
        });
        added = true;
        const addedKey = result.items?.[0]?.key;
        let current = await this.request(`${routes.cart_url}.js`);
        const codes = (current.discount_codes || []).map((item) => typeof item === 'string' ? item : item.code).filter(Boolean);
        const promotion = String(this.dataset.promotion || '').trim();
        const automatic = this.dataset.baseVolumeDiscounts === 'true';
        const filtered = automatic ? codes.filter((code) => code.toLowerCase() !== promotion.toLowerCase()) : [...codes];
        if (!automatic && promotion && !filtered.some((code) => code.toLowerCase() === promotion.toLowerCase())) filtered.push(promotion);
        if (filtered.join(',') !== codes.join(',') || (!automatic && promotion)) {
          result = await this.request(routes.cart_update_url, { discount: filtered.join(','), ...rendering });
          current = result;
        }
        if (window.FlipzyPriceTest) window.FlipzyPriceTest.verifyCart(current);
        const expected = Number(before.total_price) + quoted.total;
        // Cart-wide discounts may make the combined cart cheaper than this
        // selection. A higher or unverifiable price must be reviewed in cart.
        const verified = Number.isFinite(expected) && Number.isFinite(before.total_price) &&
          Number.isFinite(current.total_price) && typeof current.currency === 'string' &&
          before.currency === current.currency && current.total_price <= expected + 1;
        if (!verified) throw new Error(this.dataset.warning);
        // Cart notification needs one added item key; drawer consumes the full rendered sections.
        result = { ...current, sections: result.sections, key: addedKey };
        if (typeof publish === 'function') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'flipzy-base-addon', productVariantId: pack.id, cartData: result });
        if (cart && result.sections && Object.values(result.sections).every(Boolean)) {
          cart.classList.remove('is-empty');
          cart.renderContents(result);
        } else window.location.assign(routes.cart_url);
      } catch (error) {
        // The recovery owns navigation; keep this old quote out of a retry.
        if (error.reloadRequired) { reloadPending = true; return; }
        this.productForm.handleErrorMessage(added ? this.dataset.warning : error.message || this.dataset.error);
        // Never invite a duplicate retry after items have already been added.
        if (added || error.uncertainCart) window.location.assign(routes.cart_url);
      } finally {
        if (!reloadPending) {
          this.busy = false;
          this.button.removeAttribute('aria-disabled');
          this.button.classList.remove('loading');
          spinner?.classList.add('hidden');
        }
      }
    }

    disconnectedCallback() {
      document.removeEventListener('flipzy:price-test-recovered', this.priceRecovery);
      this.priceRecovery = null;
      this.events?.abort();
      this.variantUnsubscribe?.();
      this.quantityUnsubscribe?.();
      this.initialized = false;
    }
  });
}
